import React, { useState, useEffect } from 'react';
import {
    Metric, Dimension, SemanticLayerDefinition, ChartType, VisualizationConfig
} from '@/types';
import apiService from '@/services/api';
import { Button } from '@/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Loader2, Play, Save, RefreshCw, ChevronDown, ChevronRight, Info } from 'lucide-react';
import ChartRenderer from '@/components/Charts/ChartRenderer';
import { ScrollArea } from '@/components/ui/scroll-area';

import MetricDefinitionModal from './MetricDefinitionModal';

interface VisualQueryBuilderProps {
    dbConnectionId: string;
    onSave?: (widgetConfig: any) => void;
    // @ts-ignore
    initialConfig?: any;
}

const VisualQueryBuilder: React.FC<VisualQueryBuilderProps> = ({
    dbConnectionId, onSave, initialConfig: _initialConfig
}) => {
    const [definition, setDefinition] = useState<SemanticLayerDefinition | null>(null);
    const [loading, setLoading] = useState(false);

    // Selection state
    const [selectedMetrics, setSelectedMetrics] = useState<Metric[]>([]);
    const [selectedDimensions, setSelectedDimensions] = useState<Dimension[]>([]);
    const [chartType, setChartType] = useState<ChartType>('bar');

    const [queryResult, setQueryResult] = useState<any>(null);
    const [executing, setExecuting] = useState(false);

    // Grouping state
    const [expandedTables, setExpandedTables] = useState<Record<string, boolean>>({});

    const toggleTable = (tableName: string) => {
        setExpandedTables(prev => ({
            ...prev,
            [tableName]: !prev[tableName]
        }));
    };

    useEffect(() => {
        if (dbConnectionId) {
            loadDefinition();
        }
    }, [dbConnectionId]);

    const loadDefinition = async () => {
        setLoading(true);
        try {
            let def = await apiService.getSemanticDefinition(dbConnectionId);
            // Auto-discover if empty
            if (def.metrics.length === 0 && def.dimensions.length === 0) {
                try {
                    toast.info("Scanning database to auto-discover metrics...");
                    def = await apiService.autoDiscoverSemantic(dbConnectionId);
                    toast.success("Auto-discovered metrics from database tables");
                } catch (e) {
                    console.error("Auto-discovery failed", e);
                    toast.error("Auto-discovery failed. You may need to add metrics manually.");
                }
            }
            setDefinition(def);
        } catch (error) {
            toast.error("Failed to load semantic definitions");
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshSchema = async () => {
        if (!dbConnectionId) return;
        setLoading(true);
        try {
            toast.info("Rescanning database schema...");
            const def = await apiService.autoDiscoverSemantic(dbConnectionId);
            setDefinition(def);
            toast.success("Schema refreshed successfully");
        } catch (error) {
            console.error("Refresh failed", error);
            toast.error("Failed to refresh schema");
        } finally {
            setLoading(false);
        }
    };

    const handleExecute = async () => {
        if (selectedMetrics.length === 0) {
            toast.error("Select at least one metric");
            return;
        }

        setExecuting(true);
        try {
            const result = await apiService.executeSemanticQuery(
                dbConnectionId,
                selectedMetrics.map(m => m.name),
                selectedDimensions.map(d => d.name)
            );
            setQueryResult(result);
        } catch (error) {
            toast.error("Query execution failed");
        } finally {
            setExecuting(false);
        }
    };

    const handleSave = () => {
        if (!onSave) return;

        // Construct widget payload
        const vizConfig: VisualizationConfig = {
            chart_type: chartType,
            x_axis: selectedDimensions.length > 0 ? selectedDimensions[0].name : 'index',
            y_axis: selectedMetrics.map(m => m.name),
        };

        onSave({
            title: `${selectedMetrics.map(m => m.name).join(', ')} by ${selectedDimensions.map(d => d.name).join(', ') || 'Total'}`,
            type: 'chart',
            db_connection_id: dbConnectionId,
            viz_config: vizConfig,
            sql_query: queryResult?.sql // The generated SQL
        });
    };

    // Drag and Drop Logic (using HTML5)
    const onDragStart = (e: React.DragEvent, item: Metric | Dimension, type: 'metric' | 'dimension') => {
        e.dataTransfer.setData("application/json", JSON.stringify({ item, type }));
    };

    const onDrop = (e: React.DragEvent, targetType: 'metric' | 'dimension') => {
        e.preventDefault();
        const dataString = e.dataTransfer.getData("application/json");
        if (!dataString) return;

        try {
            const data = JSON.parse(dataString);
            if (data.type !== targetType) return;

            if (targetType === 'metric') {
                if (!selectedMetrics.find(m => m.name === data.item.name)) {
                    setSelectedMetrics([...selectedMetrics, data.item]);
                }
            } else {
                if (!selectedDimensions.find(d => d.name === data.item.name)) {
                    setSelectedDimensions([...selectedDimensions, data.item]);
                }
            }
        } catch (e) {
            console.error("Drag drop parse error", e);
        }
    };

    // Grouping Logic
    const activeTable = selectedMetrics.length > 0 ? selectedMetrics[0].table_name :
        (selectedDimensions.length > 0 && selectedMetrics.length === 0) ? selectedDimensions[0].table_name : null;

    // Filter available options based on active table (Single Table Restriction)
    const availableMetrics = definition?.metrics.filter(m => !activeTable || m.table_name === activeTable) || [];
    const availableDimensions = definition?.dimensions.filter(d => !activeTable || d.table_name === activeTable) || [];

    const groupItems = <T extends { table_name: string }>(items: T[]) => {
        return items.reduce((acc, item) => {
            if (!acc[item.table_name]) acc[item.table_name] = [];
            acc[item.table_name].push(item);
            return acc;
        }, {} as Record<string, T[]>);
    };

    const groupedMetrics = groupItems(availableMetrics);
    const groupedDimensions = groupItems(availableDimensions);
    const allTables = Array.from(new Set([...Object.keys(groupedMetrics), ...Object.keys(groupedDimensions)])).sort();

    // Auto-expand active table
    useEffect(() => {
        if (activeTable) {
            setExpandedTables(prev => ({ ...prev, [activeTable]: true }));
        }
    }, [activeTable]);

    return (
        <div className="flex h-full min-h-[500px] w-full gap-6 p-1">
            {/* Sidebar: Fields */}
            <div className="w-[280px] shrink-0 border-r pr-6 flex flex-col gap-4 h-full">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg tracking-tight">Data Fields</h3>
                        <button
                            onClick={handleRefreshSchema}
                            disabled={loading}
                            className="p-1 hover:bg-accent rounded-full text-muted-foreground transition-colors"
                            title="Refresh Schema"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                    <MetricDefinitionModal
                        dbConnectionId={dbConnectionId}
                        onMetricAdded={loadDefinition}
                    />
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center flex-1 text-muted-foreground animate-pulse gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <span className="text-sm">Loading schema...</span>
                    </div>
                ) : (
                    <ScrollArea className="flex-1 -mr-4 pr-4">
                        <div className="space-y-4 pb-4">
                            {/* Active Filter Banner */}
                            {activeTable && (
                                <div className="mx-1 p-2 bg-blue-50/50 border border-blue-100 dark:bg-blue-900/10 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2 animate-in fade-in slide-in-from-top-2">
                                    <Info className="h-4 w-4 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="font-semibold">Filtered for: {activeTable}</p>
                                        <p className="opacity-90">Only showing fields from the same table.</p>
                                    </div>
                                </div>
                            )}

                            {allTables.length === 0 && (
                                <div className="text-sm text-center text-muted-foreground py-8">
                                    No fields found. Try refreshing the schema.
                                </div>
                            )}

                            {allTables.map(tableName => (
                                <div key={tableName} className="border rounded-md overflow-hidden bg-card/50">
                                    <button
                                        onClick={() => toggleTable(tableName)}
                                        className="w-full flex items-center justify-between p-2.5 bg-muted/40 hover:bg-muted/60 transition-colors text-sm font-medium"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="truncate max-w-[180px]" title={tableName}>{tableName}</span>
                                            <span className="text-xs text-muted-foreground bg-background border px-1.5 rounded-full">
                                                {(groupedMetrics[tableName]?.length || 0) + (groupedDimensions[tableName]?.length || 0)}
                                            </span>
                                        </div>
                                        {expandedTables[tableName] ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                                    </button>

                                    {expandedTables[tableName] && (
                                        <div className="p-2 space-y-4 bg-background">
                                            {/* Metrics Group */}
                                            {groupedMetrics[tableName]?.length > 0 && (
                                                <div>
                                                    <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                                                        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                                        Metrics
                                                    </h4>
                                                    <div className="space-y-1 pl-1">
                                                        {groupedMetrics[tableName].map(m => (
                                                            <div key={m.name}
                                                                draggable
                                                                onDragStart={(e) => onDragStart(e, m, 'metric')}
                                                                className="group flex items-center justify-between p-2 rounded-md border bg-card hover:bg-green-50/50 hover:border-green-200 dark:hover:bg-green-900/10 dark:hover:border-green-800 shadow-sm transition-all cursor-move active:cursor-grabbing"
                                                            >
                                                                <span className="text-xs font-medium truncate" title={m.name}>{m.name.replace(`${tableName}_`, '')}</span>
                                                                <span className="text-[10px] text-muted-foreground/70 bg-muted px-1 rounded">{m.aggregation}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Dimensions Group */}
                                            {groupedDimensions[tableName]?.length > 0 && (
                                                <div>
                                                    <h4 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2 mt-3 flex items-center gap-2">
                                                        <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                                                        Dimensions
                                                    </h4>
                                                    <div className="space-y-1 pl-1">
                                                        {groupedDimensions[tableName].map(d => (
                                                            <div key={d.name}
                                                                draggable
                                                                onDragStart={(e) => onDragStart(e, d, 'dimension')}
                                                                className="group flex items-center justify-between p-2 rounded-md border bg-card hover:bg-blue-50/50 hover:border-blue-200 dark:hover:bg-blue-900/10 dark:hover:border-blue-800 shadow-sm transition-all cursor-move active:cursor-grabbing"
                                                            >
                                                                <span className="text-xs font-medium truncate" title={d.name}>{d.name}</span>
                                                                <span className="text-[10px] text-muted-foreground/70 bg-muted px-1 rounded">{d.data_type}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </ScrollArea>
                )}
            </div>

            {/* Main Area: Builder & Preview */}
            <div className="flex-1 flex flex-col gap-6 h-full min-w-0">
                {/* Query Config Zones */}
                <div className="grid grid-cols-2 gap-6 shrink-0">
                    <div
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => onDrop(e, 'dimension')}
                        className={`
                            relative rounded-xl border-2 border-dashed p-4 min-h-[140px] transition-colors
                            ${selectedDimensions.length === 0 ? 'border-muted-foreground/25 bg-muted/30' : 'border-blue-200 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-800'}
                            hover:bg-accent/50 hover:border-accent-foreground/50
                        `}
                    >
                        <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-blue-500" />
                            X-Axis (Dimensions)
                        </h4>

                        {selectedDimensions.length === 0 ? (
                            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground pointer-events-none">
                                <span className="text-sm">Drag dimensions here</span>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2 relative z-10">
                                {selectedDimensions.map(d => (
                                    <div key={d.name} className="flex items-center gap-2 px-3 py-1.5 bg-background border shadow-sm rounded-full text-sm animate-in fade-in zoom-in duration-200">
                                        <span className="text-muted-foreground text-xs font-mono">DIM</span>
                                        <span className="font-medium">{d.name}</span>
                                        <button
                                            onClick={() => setSelectedDimensions(selectedDimensions.filter(x => x.name !== d.name))}
                                            className="ml-1 h-4 w-4 rounded-full hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center transition-colors"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => onDrop(e, 'metric')}
                        className={`
                            relative rounded-xl border-2 border-dashed p-4 min-h-[140px] transition-colors
                            ${selectedMetrics.length === 0 ? 'border-muted-foreground/25 bg-muted/30' : 'border-green-200 bg-green-50/50 dark:bg-green-900/10 dark:border-green-800'}
                            hover:bg-accent/50 hover:border-accent-foreground/50
                        `}
                    >
                        <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-green-500" />
                            Y-Axis (Metrics)
                        </h4>

                        {selectedMetrics.length === 0 ? (
                            <div className="absolute inset-0 flex items-center justify-center text-muted-foreground pointer-events-none">
                                <span className="text-sm">Drag metrics here</span>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2 relative z-10">
                                {selectedMetrics.map(m => (
                                    <div key={m.name} className="flex items-center gap-2 px-3 py-1.5 bg-background border shadow-sm rounded-full text-sm animate-in fade-in zoom-in duration-200">
                                        <span className="text-muted-foreground text-xs font-mono">MET</span>
                                        <span className="font-medium">{m.name}</span>
                                        <span className="text-xs text-muted-foreground/70 bg-muted px-1 rounded">{m.aggregation}</span>
                                        <button
                                            onClick={() => setSelectedMetrics(selectedMetrics.filter(x => x.name !== m.name))}
                                            className="ml-1 h-4 w-4 rounded-full hover:bg-destructive hover:text-destructive-foreground flex items-center justify-center transition-colors"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions & Preview */}
                <div className="flex-1 flex flex-col min-h-0 bg-background rounded-lg border shadow-sm overflow-hidden">
                    <div className="flex items-center justify-between p-4 border-b bg-muted/10 shrink-0">
                        <div className="flex items-center gap-4">
                            <Select value={chartType} onValueChange={(v: ChartType) => setChartType(v)}>
                                <SelectTrigger className="w-[200px] h-9">
                                    <SelectValue placeholder="Select Chart Type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="bar">Bar Chart</SelectItem>
                                    <SelectItem value="line">Line Chart</SelectItem>
                                    <SelectItem value="area">Area Chart</SelectItem>
                                    <SelectItem value="pie">Pie Chart</SelectItem>
                                    <SelectItem value="scatter">Scatter Plot</SelectItem>
                                    <SelectItem value="table">Table View</SelectItem>
                                </SelectContent>
                            </Select>

                            <div className="w-px h-6 bg-border" />

                            <span className="text-xs text-muted-foreground">
                                {queryResult ? `${queryResult.rows?.length || 0} rows found` : 'Ready to run'}
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button
                                onClick={handleExecute}
                                disabled={executing || selectedMetrics.length === 0}
                                size="sm"
                                className={executing ? 'opacity-80' : ''}
                            >
                                {executing ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-2 h-3.5 w-3.5 fill-current" />}
                                Run Query
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={!queryResult}
                                variant="outline"
                                size="sm"
                            >
                                <Save className="mr-2 h-3.5 w-3.5" />
                                Save to Dashboard
                            </Button>
                        </div>
                    </div>

                    <div className="flex-1 relative min-h-0 bg-white dark:bg-black/5">
                        {queryResult ? (
                            <div className="absolute inset-0 p-6">
                                <ChartRenderer
                                    chartType={chartType}
                                    columns={queryResult.columns || []}
                                    rows={queryResult.rows || []}
                                    rowCount={queryResult.rows?.length || 0}
                                    vizConfig={{ colors: undefined }}
                                />
                            </div>
                        ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground gap-4 bg-muted/5">
                                <div className="p-4 rounded-full bg-background border shadow-sm">
                                    <Play className="h-8 w-8 text-muted-foreground/50" />
                                </div>
                                <div className="text-center">
                                    <p className="font-medium">No Data to Display</p>
                                    <p className="text-sm opacity-70">Configure your metrics and dimensions above, then click Run Query.</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VisualQueryBuilder;
